import React from 'react';

import Dialog from '@dashboard/components/Dialog';
import Form from '@dashboard/components/Formik/Form';
import { countries } from '@dashboard/services/countries/countries';
import { fieldLabelText, multiSelectOptionValue } from '@repo/lib';

const FORM_TYPES = {
  text: 'text',
  email: 'email',
  number: 'number',
  textarea: 'textarea',
  phone: 'tel',
  date: 'date',
  time: 'time',
  'date-time': 'datetime-local',
  address: 'textarea',
};

const questionId = question => String(question._id);

function valueOf(row, question) {
  if (question.type === 'name') return row.name ?? '';
  if (question.type === 'email') return row.email ?? '';
  const answer = (row.answers ?? []).find(a => a.fieldId === questionId(question));
  if (question.element === 'multi-select') return answer?.values ?? [];
  return answer?.value ?? '';
}

function formField(question, row) {
  const base = {
    name: questionId(question),
    label: fieldLabelText(question.label),
    required: Boolean(question.required),
    showRequired: true,
    _value: valueOf(row, question),
  };
  const options = question.options ?? [];
  switch (question.element) {
    case 'select':
      return { ...base, type: 'select', options: [{ label: '', value: '' }, ...options] };
    case 'multiple-choice':
      return {
        ...base,
        type: 'select',
        options: [
          { label: '', value: '' },
          ...options.map(o => ({ label: o.label, value: o.label })),
        ],
      };
    case 'multi-select':
      return {
        ...base,
        type: 'listbox',
        multiple: true,
        summary: true,
        placeholder: 'Select options',
        options: options.map(o => ({ name: o.label, value: multiSelectOptionValue(o) })),
      };
    case 'checkbox':
      return { ...base, type: 'checkbox', _value: base._value === 'true' || base._value === true };
    case 'country':
      return {
        ...base,
        type: 'select',
        options: [
          { label: 'Select country', value: '' },
          ...countries.map(country => ({ label: country.name, value: country.alpha2 })),
        ],
      };
    default:
      return { ...base, type: FORM_TYPES[question.element] ?? 'text' };
  }
}

// An address is edited as text here; left unchanged, it keeps the address the booker entered.
export function guestInputFromValues(questions, values, row = {}) {
  const answers = questions
    .filter(q => q.type !== 'name' && q.type !== 'email')
    .map(q => {
      const value = values[questionId(q)];
      const before = (row.answers ?? []).find(a => a.fieldId === questionId(q));
      if (q.element === 'address' && before?.meta && value === before.value) {
        return { fieldId: questionId(q), value, meta: before.meta };
      }
      return Array.isArray(value)
        ? { fieldId: questionId(q), values: value }
        : {
            fieldId: questionId(q),
            value: value === undefined || value === null ? '' : String(value),
          };
    });
  const byType = type => {
    const question = questions.find(q => q.type === type);
    return question ? String(values[questionId(question)] ?? '') : undefined;
  };
  return { name: byType('name'), email: byType('email'), answers };
}

/**
 * Edit attendee N: the ticket shown, never changed (moving a guest to another ticket type would change
 * what was paid), then the guest questions. Saving updates the ticket and its PDF and sends nothing.
 */
export default function EditGuestDialog({ guests, row, open, onOpenChange, onSave }) {
  if (!row) return null;
  const questions = guests.questions ?? [];
  const fields = [
    {
      type: 'text',
      name: 'ticket',
      label: 'Ticket',
      disabled: true,
      helpText: "An attendee's ticket can't be changed.",
      _value: row.ticketTypeName ?? '',
    },
    ...questions.map(question => formField(question, row)),
  ];

  return (
    <Dialog
      trigger={null}
      title={`Edit attendee ${row.position}`}
      open={open}
      onOpenChange={onOpenChange}
      onCancel={() => onOpenChange(false)}
      confirmButton={null}
      cancelButton={null}
    >
      {({ close }) => (
        <Form
          fields={fields}
          initialValues={Object.fromEntries(
            fields.filter(f => f.type !== 'custom').map(f => [f.name, f._value]),
          )}
          onSubmit={async values => {
            try {
              await onSave(row, guestInputFromValues(questions, values, row));
              onOpenChange(false);
              return { data: true };
            } catch (error) {
              return { error };
            }
          }}
          cancelButton={{ label: 'Cancel', appearance: 'light', onClick: close }}
          formSubmitAlign="end"
        />
      )}
    </Dialog>
  );
}
